import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  Download, 
  CheckCircle, 
  AlertTriangle, 
  Clock,
  User,
  Building2,
  Stethoscope,
  Calendar,
  Phone,
  MapPin,
  FileText,
  Settings,
  History,
  Loader2,
  AlertCircle,
  ExternalLink,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';

const MedicinaDireta = () => {
  const { user, api } = useAuth();
  const isAdmin = ['admin', 'master_admin', 'MASTER_ADMIN'].includes(user?.perfil);
  
  // State
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [pacientes, setPacientes] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [config, setConfig] = useState(null);
  
  // Filters
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroUrgencia, setFiltroUrgencia] = useState('todos');
  
  // Modals
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  
  // Loading states
  const [importando, setImportando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [salvandoConfig, setSalvandoConfig] = useState(false);
  
  // Config form
  const [configForm, setConfigForm] = useState({
    habilitado: false,
    api_url: '',
    api_key: '',
    intervalo_polling_minutos: 15,
    auto_importar: false
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const [statusRes, pacientesRes, historicoRes] = await Promise.all([
        api.get('/medicina-direta/status'),
        api.get('/medicina-direta/pacientes-disponiveis'),
        api.get('/medicina-direta/historico?limit=50')
      ]);
      
      setStatus(statusRes.data);
      setPacientes(pacientesRes.data.pacientes || []);
      setHistorico(historicoRes.data.historico || []);
    } catch (error) {
      toast.error('Erro ao carregar dados da integração');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchConfig = async () => {
    if (!api) return;
    try {
      const res = await api.get('/medicina-direta/config');
      setConfig(res.data);
      setConfigForm({
        habilitado: res.data.habilitado || false,
        api_url: res.data.api_url || '',
        api_key: '',
        intervalo_polling_minutos: res.data.intervalo_polling_minutos || 15,
        auto_importar: res.data.auto_importar || false
      });
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  useEffect(() => {
    fetchData();
    if (isAdmin) {
      fetchConfig();
    }
  }, [fetchData, isAdmin]);

  // Handlers
  const handleSincronizar = async () => {
    setSincronizando(true);
    try {
      const res = await api.post('/medicina-direta/sincronizar');
      toast.success(res.data.mensagem);
      fetchData();
    } catch (error) {
      toast.error('Erro ao sincronizar');
    } finally {
      setSincronizando(false);
    }
  };

  const handleImportarPaciente = async (idExterno) => {
    setImportando(true);
    try {
      const res = await api.post(`/medicina-direta/importar/${idExterno}`);
      toast.success(res.data.mensagem);
      setShowImportModal(false);
      setSelectedPaciente(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar paciente');
    } finally {
      setImportando(false);
    }
  };

  const handleImportarTodos = async () => {
    if (!window.confirm('Deseja importar todos os pacientes disponíveis?')) return;
    
    setImportando(true);
    try {
      const res = await api.post('/medicina-direta/importar-todos');
      toast.success(`${res.data.importados} pacientes importados com sucesso!`);
      fetchData();
    } catch (error) {
      toast.error('Erro ao importar pacientes em lote');
    } finally {
      setImportando(false);
    }
  };

  const handleSaveConfig = async () => {
    setSalvandoConfig(true);
    try {
      await api.put('/medicina-direta/config', configForm);
      toast.success('Configuração salva com sucesso');
      setShowConfigModal(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSalvandoConfig(false);
    }
  };

  // Filter patients
  const pacientesFiltrados = pacientes.filter(p => {
    if (filtroTipo !== 'todos' && p.tipo_cuidado !== filtroTipo) return false;
    if (filtroUrgencia !== 'todos' && p.urgencia !== filtroUrgencia) return false;
    return true;
  });

  // Helpers
  const getUrgenciaBadge = (urgencia) => {
    const variants = {
      urgente: 'bg-red-100 text-red-700 border-red-200',
      alta: 'bg-orange-100 text-orange-700 border-orange-200',
      normal: 'bg-green-100 text-green-700 border-green-200'
    };
    const labels = {
      urgente: 'Urgente',
      alta: 'Alta',
      normal: 'Normal'
    };
    return (
      <Badge variant="outline" className={variants[urgencia] || variants.normal}>
        {labels[urgencia] || urgencia}
      </Badge>
    );
  };

  const getTipoCuidadoBadge = (tipo) => {
    const variants = {
      home_care: 'bg-blue-100 text-blue-700',
      paliativo: 'bg-purple-100 text-purple-700',
      pos_cirurgico: 'bg-teal-100 text-teal-700'
    };
    const labels = {
      home_care: 'Home Care',
      paliativo: 'Paliativo',
      pos_cirurgico: 'Pós-Cirúrgico'
    };
    return (
      <Badge variant="outline" className={variants[tipo] || 'bg-gray-100'}>
        {labels[tipo] || tipo}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? new Date(dateStr + 'T12:00:00') : new Date(dateStr); return d.toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR', { timeZone: 'America/Manaus' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold">Medicina Direta</h1>
            <p className="text-muted-foreground">
              Integração com sistema EMR para importação de pacientes
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleSincronizar}
              disabled={sincronizando}
            >
              {sincronizando ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sincronizar
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => setShowConfigModal(true)}
              >
                <Settings className="h-4 w-4 mr-2" />
                Configurar
              </Button>
            )}
          </div>
        </div>

        {/* Mock Warning */}
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Integração Mockada</AlertTitle>
          <AlertDescription className="text-amber-700">
            Esta é uma implementação de demonstração. Os dados exibidos são simulados.
            Configure as credenciais reais da API para ativar a integração.
          </AlertDescription>
        </Alert>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className="text-2xl font-bold">
                    {status?.habilitado ? (
                      <span className="text-green-600 flex items-center gap-2">
                        <CheckCircle className="h-5 w-5" />
                        Ativo
                      </span>
                    ) : (
                      <span className="text-gray-500 flex items-center gap-2">
                        <AlertCircle className="h-5 w-5" />
                        Inativo
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pendentes</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {status?.pacientes_pendentes || 0}
                  </p>
                </div>
                <User className="h-8 w-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Importados Hoje</p>
                  <p className="text-2xl font-bold text-green-600">
                    {status?.pacientes_importados_hoje || 0}
                  </p>
                </div>
                <Download className="h-8 w-8 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Última Verificação</p>
                  <p className="text-sm font-medium">
                    {status?.ultima_verificacao 
                      ? formatDateTime(status.ultima_verificacao)
                      : 'Nunca'}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-gray-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="disponiveis">
          <TabsList>
            <TabsTrigger value="disponiveis" className="gap-2">
              <User className="h-4 w-4" />
              Pacientes Disponíveis
              {pacientes.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {pacientes.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="historico" className="gap-2">
              <History className="h-4 w-4" />
              Histórico
            </TabsTrigger>
          </TabsList>

          {/* Pacientes Disponíveis */}
          <TabsContent value="disponiveis" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex items-center gap-2">
                    <Label>Tipo:</Label>
                    <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        <SelectItem value="home_care">Home Care</SelectItem>
                        <SelectItem value="paliativo">Paliativo</SelectItem>
                        <SelectItem value="pos_cirurgico">Pós-Cirúrgico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Label>Urgência:</Label>
                    <Select value={filtroUrgencia} onValueChange={setFiltroUrgencia}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todas</SelectItem>
                        <SelectItem value="urgente">Urgente</SelectItem>
                        <SelectItem value="alta">Alta</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex-1" />

                  {isAdmin && pacientesFiltrados.length > 0 && (
                    <Button onClick={handleImportarTodos} disabled={importando}>
                      {importando ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Importar Todos ({pacientesFiltrados.length})
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Patient List */}
            {pacientesFiltrados.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  <p className="text-lg font-medium">Nenhum paciente pendente</p>
                  <p className="text-muted-foreground">
                    Todos os pacientes disponíveis já foram importados
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {pacientesFiltrados.map((paciente) => (
                  <Card key={paciente.id_externo} className="hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                        {/* Patient Info */}
                        <div className="flex-1 space-y-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-lg font-semibold">{paciente.nome}</h3>
                              <p className="text-sm text-muted-foreground">
                                ID: {paciente.id_externo}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              {getUrgenciaBadge(paciente.urgencia)}
                              {getTipoCuidadoBadge(paciente.tipo_cuidado)}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2">
                              <Stethoscope className="h-4 w-4 text-muted-foreground" />
                              <span className="text-muted-foreground">Diagnóstico:</span>
                              <span className="font-medium">{paciente.diagnostico_principal || '-'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <span className="text-muted-foreground">Hospital:</span>
                              <span className="font-medium">{paciente.origem_hospitalar || '-'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <span className="text-muted-foreground">Alta Hospitalar:</span>
                              <span className="font-medium">{formatDate(paciente.data_alta_hospital)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <span className="text-muted-foreground">Médico:</span>
                              <span className="font-medium">{paciente.medico_responsavel || '-'}</span>
                            </div>
                          </div>

                          {paciente.observacoes && (
                            <div className="flex items-start gap-2 text-sm bg-muted/50 p-2 rounded">
                              <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                              <span>{paciente.observacoes}</span>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex lg:flex-col gap-2">
                          <Button
                            onClick={() => {
                              setSelectedPaciente(paciente);
                              setShowImportModal(true);
                            }}
                            className="flex-1 lg:flex-none"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Importar
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setSelectedPaciente(paciente);
                              setShowImportModal(true);
                            }}
                            className="flex-1 lg:flex-none"
                          >
                            Ver Detalhes
                            <ChevronRight className="h-4 w-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Histórico */}
          <TabsContent value="historico">
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Importações</CardTitle>
                <CardDescription>
                  Registro de todos os pacientes importados do sistema Medicina Direta
                </CardDescription>
              </CardHeader>
              <CardContent>
                {historico.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhuma importação realizada ainda</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 font-medium">Data</th>
                          <th className="text-left p-3 font-medium">Paciente</th>
                          <th className="text-left p-3 font-medium">ID Externo</th>
                          <th className="text-left p-3 font-medium">Importado por</th>
                          <th className="text-left p-3 font-medium">Status</th>
                          <th className="text-left p-3 font-medium">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historico.map((item) => (
                          <tr key={item.id} className="border-b hover:bg-muted/50">
                            <td className="p-3 text-sm">
                              {formatDateTime(item.data_importacao)}
                            </td>
                            <td className="p-3">
                              <div>
                                <p className="font-medium">{item.dados_importados?.nome || '-'}</p>
                                <p className="text-sm text-muted-foreground">
                                  {item.dados_importados?.diagnostico}
                                </p>
                              </div>
                            </td>
                            <td className="p-3 text-sm font-mono">
                              {item.id_externo}
                            </td>
                            <td className="p-3 text-sm">
                              {item.importado_por_nome || '-'}
                            </td>
                            <td className="p-3">
                              {item.sucesso ? (
                                <Badge variant="outline" className="bg-green-50 text-green-700">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Sucesso
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-red-50 text-red-700">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  Erro
                                </Badge>
                              )}
                            </td>
                            <td className="p-3">
                              {item.paciente_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(`/pacientes/${item.paciente_id}`, '_blank')}
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Importar Paciente</DialogTitle>
            <DialogDescription>
              Revise os dados antes de importar para o sistema
            </DialogDescription>
          </DialogHeader>

          {selectedPaciente && (
            <div className="space-y-4">
              {/* Patient Header */}
              <div className="flex items-start justify-between p-4 bg-muted rounded-lg">
                <div>
                  <h3 className="text-lg font-semibold">{selectedPaciente.nome}</h3>
                  <p className="text-sm text-muted-foreground">CPF: {selectedPaciente.cpf}</p>
                </div>
                <div className="flex gap-2">
                  {getUrgenciaBadge(selectedPaciente.urgencia)}
                  {getTipoCuidadoBadge(selectedPaciente.tipo_cuidado)}
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-1">
                  <p className="text-muted-foreground">Data de Nascimento</p>
                  <p className="font-medium">{formatDate(selectedPaciente.data_nascimento)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground">Telefone</p>
                  <p className="font-medium">{selectedPaciente.telefone || '-'}</p>
                </div>
                <div className="space-y-1 col-span-2">
                  <p className="text-muted-foreground">Endereço</p>
                  <p className="font-medium">
                    {selectedPaciente.endereco}, {selectedPaciente.cidade} - {selectedPaciente.uf}
                  </p>
                </div>
                <div className="space-y-1 col-span-2">
                  <p className="text-muted-foreground">Diagnóstico Principal</p>
                  <p className="font-medium">
                    {selectedPaciente.diagnostico_principal}
                    {selectedPaciente.cid10 && ` (${selectedPaciente.cid10})`}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground">Hospital de Origem</p>
                  <p className="font-medium">{selectedPaciente.origem_hospitalar || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground">Data Alta Hospitalar</p>
                  <p className="font-medium">{formatDate(selectedPaciente.data_alta_hospital)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground">Médico Responsável</p>
                  <p className="font-medium">{selectedPaciente.medico_responsavel || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground">CRM</p>
                  <p className="font-medium">{selectedPaciente.crm_medico || '-'}</p>
                </div>
              </div>

              {selectedPaciente.observacoes && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-800">Observações:</p>
                  <p className="text-sm text-blue-700">{selectedPaciente.observacoes}</p>
                </div>
              )}

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Ao importar, será criado um novo paciente e uma internação com os serviços padrão.
                </AlertDescription>
              </Alert>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => handleImportarPaciente(selectedPaciente?.id_externo)}
              disabled={importando}
            >
              {importando ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Confirmar Importação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configuração da Integração</DialogTitle>
            <DialogDescription>
              Configure as credenciais e opções de sincronização
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700">
                Integração mockada. Credenciais serão armazenadas mas não utilizadas até a API real.
              </AlertDescription>
            </Alert>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Integração Habilitada</Label>
                <p className="text-sm text-muted-foreground">
                  Ativar sincronização automática
                </p>
              </div>
              <Switch
                checked={configForm.habilitado}
                onCheckedChange={(checked) => setConfigForm({...configForm, habilitado: checked})}
              />
            </div>

            <div className="space-y-2">
              <Label>URL da API</Label>
              <Input
                placeholder="https://api.medicinadireta.com.br"
                value={configForm.api_url}
                onChange={(e) => setConfigForm({...configForm, api_url: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Chave da API</Label>
              <Input
                type="password"
                placeholder="••••••••••••"
                value={configForm.api_key}
                onChange={(e) => setConfigForm({...configForm, api_key: e.target.value})}
              />
              {config?.api_key && (
                <p className="text-xs text-muted-foreground">
                  Chave atual: {config.api_key}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Intervalo de Sincronização (minutos)</Label>
              <Select
                value={configForm.intervalo_polling_minutos.toString()}
                onValueChange={(value) => setConfigForm({...configForm, intervalo_polling_minutos: parseInt(value)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 minutos</SelectItem>
                  <SelectItem value="15">15 minutos</SelectItem>
                  <SelectItem value="30">30 minutos</SelectItem>
                  <SelectItem value="60">1 hora</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Auto-importar</Label>
                <p className="text-sm text-muted-foreground">
                  Importar novos pacientes automaticamente
                </p>
              </div>
              <Switch
                checked={configForm.auto_importar}
                onCheckedChange={(checked) => setConfigForm({...configForm, auto_importar: checked})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveConfig} disabled={salvandoConfig}>
              {salvandoConfig ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Settings className="h-4 w-4 mr-2" />
              )}
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default MedicinaDireta;
